import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  X, User, Heart, Briefcase, FileText, AlertTriangle,
  Globe, GripHorizontal
} from 'lucide-react';
import { ExpandableSection, EntityField } from './entity-detail/EntityDetailPrimitives';
import {
  groupNameClassifications, matchedNameType, nameTypeClass, listingIdentity, sameListing,
} from '@/lib/nameTypes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

export default function EntityDetailModal({
  entityId, source, matchData, relatedListings = [], listingStatus = null,
  onOpenListing, onClose,
}) {
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recordNotice, setRecordNotice] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    personal: true,
    names: true,
    family: false,
    positions: true,
    additional: true
  });

  // Drag state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const dragStart = useRef({ x: 0, y: 0 });

  const onMouseDown = useCallback((e) => {
    if (e.target.closest('button')) return; // Don't drag when clicking buttons
    setIsDragging(true);
    dragStart.current = { x: e.clientX - position.x, y: e.clientY - position.y };
  }, [position]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDragging) return;
      setPosition({
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y
      });
    };
    const onMouseUp = () => setIsDragging(false);

    if (isDragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDragging]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && onClose) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    const loadEntity = async () => {
      setLoading(true);
      setRecordNotice(null);

      // The exact record that produced this result, resolved on its listing ID
      // AND its authority. No name search, no sibling record: a detail panel
      // that quietly shows a different authority's record is not reviewable.
      const identity = listingIdentity(matchData || {});
      const eid = entityId || identity.recordId;
      const authority = source || identity.authority;

      if (eid) {
        try {
          const response = await axios.get(`${API}/entity/${encodeURIComponent(eid)}`, {
            params: authority ? { source: authority } : {},
            headers: getAuthHeaders(),
          });
          if (response.data) {
            setEntity(response.data);
            setLoading(false);
            return;
          }
        } catch (err) {
          const status = err.response?.status;
          setRecordNotice(
            status === 404
              ? `Listing ${eid}${authority ? ` on ${authority}` : ''} is no longer in the watchlist data held here. `
                + 'Showing only what was captured on the screening record.'
              : `The source record (${eid}${authority ? ` · ${authority}` : ''}) could not be retrieved. `
                + 'Showing only what was captured on the screening record.'
          );
        }
      } else if (matchData) {
        setRecordNotice('This result carries no source listing ID, so there is no record to open. '
          + 'Showing only what was captured on the screening record.');
      }

      // Never substitute another record: fall back to this result's own data.
      if (matchData) {
        const details = matchData.details || matchData.additional_info || {};
        setEntity({
          name: matchData.matched_name,
          source: matchData.source,
          entity_id: details.source_record_id || details.entity_id || null,
          entity_type: matchData.entity_type || details.entity_type,
          details,
          from_screening_record: true,
        });
      }

      setLoading(false);
    };

    loadEntity();
  }, [entityId, source, matchData]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const renderSection = (title, icon, sectionKey, content) => (
    <ExpandableSection
      title={title}
      icon={icon}
      isExpanded={expandedSections[sectionKey]}
      onToggle={() => toggleSection(sectionKey)}
    >
      {content}
    </ExpandableSection>
  );

  const renderField = (label, value) => <EntityField label={label} value={value} />;

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white mx-auto shadow-2xl p-6"
          style={{ marginTop: '5vh' }}>
          <div className="text-center py-12 text-slate-500">Loading entity details...</div>
        </Card>
      </div>
    );
  }

  if (!entity) {
    return (
      <div className="fixed inset-0 z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <Card className="w-full max-w-4xl bg-white mx-auto shadow-2xl p-6"
          style={{ marginTop: '5vh' }}>
          <div className="text-center py-12">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <p className="text-slate-500">Entity not found</p>
            <Button onClick={onClose} className="mt-4">Close</Button>
          </div>
        </Card>
      </div>
    );
  }

  const details = entity.details || {};
  const nameGroups = groupNameClassifications(
    entity.name_classifications || details.name_classifications || []
  );
  const otherListings = (relatedListings || []).filter(
    (listing) => !sameListing(listing, matchData || entity)
  );
  const isOFAC = entity.source === 'OFAC SDN';
  const isPEP = entity.source === 'OpenSanctions PEP';
  const isUNSCR = entity.source === 'UNSCR';

  // The explanation of this result as it stood when the screening ran. It is
  // read from the stored scan, never from today's watchlist, so a later sync
  // cannot rewrite it.
  const provenance = matchData?.details?.screening_provenance || null;
  const liveRecord = entity.from_screening_record ? null : entity;
  const recordChanged = Boolean(
    provenance && liveRecord && provenance.primary_name &&
    liveRecord.name && provenance.primary_name !== liveRecord.name
  );

  return (
    <div className="fixed inset-0 z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Card
        ref={dragRef}
        className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white mx-auto shadow-2xl"
        style={{
          marginTop: '5vh',
          transform: `translate(${position.x}px, ${position.y}px)`,
        }}
      >
        {/* Draggable Header */}
        <div
          className="sticky top-0 bg-white border-b border-slate-200 p-6 z-10 select-none"
          onMouseDown={onMouseDown}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <GripHorizontal className="w-5 h-5 text-slate-400 mt-1 shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-slate-900">{entity.name}</h2>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <Badge className={isOFAC ? 'bg-rose-100 text-rose-800' : 'bg-blue-100 text-blue-800'}>
                    {entity.source}
                  </Badge>
                  {entity.entity_type && (
                    <Badge variant="outline">{entity.entity_type}</Badge>
                  )}
                  {details.wikidata_enriched && (
                    <Badge className="bg-green-100 text-green-800">Wikidata Enriched</Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-2 font-mono" data-testid="entity-record-identity">
                  Listing ID {entity.entity_id || '—'} · {entity.source || 'source not recorded'}
                </p>
                {matchData?.matched_name && (
                  <div className="text-xs text-slate-600 mt-1 flex items-center gap-2" data-testid="entity-matched-name">
                    <span>Matched on</span>
                    <span className="font-medium text-slate-900">{matchData.matched_name}</span>
                    <Badge variant="outline" className={`text-[10px] border ${nameTypeClass(matchedNameType(matchData))}`}>
                      {matchedNameType(matchData)}
                    </Badge>
                  </div>
                )}
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="entity-modal-close-btn">
              <X className="w-5 h-5" />
            </Button>
          </div>
          {recordNotice && (
            <div
              className="mt-4 flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200"
              data-testid="entity-record-notice"
            >
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800 leading-relaxed">{recordNotice}</p>
            </div>
          )}
        </div>

        <div className="p-6 space-y-4">
          {/* Why this result was produced — frozen at screening time */}
          {provenance && (
            <div
              className="border border-slate-300 rounded-md bg-slate-50"
              data-testid="screening-provenance"
            >
              <div className="px-4 py-3 border-b border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900">
                  Why this result was produced
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Recorded when the screening ran
                  {provenance.screened_at && ` on ${new Date(provenance.screened_at).toLocaleString()}`}.
                  A later watchlist sync does not change this explanation.
                </p>
              </div>
              <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3">
                {[
                  ['Source', provenance.source],
                  ['Listing ID', provenance.listing_id],
                  ['Matched name', provenance.matched_name],
                  ['Name type', provenance.matched_name_type],
                  ['Primary name then', provenance.primary_name],
                  ['Match method', provenance.match_method],
                  ['Name score', provenance.name_score != null ? `${provenance.name_score}%` : null],
                  ['List data as of', provenance.list_data_as_of
                    ? String(provenance.list_data_as_of).slice(0, 10) : null],
                ].filter(([, value]) => value).map(([label, value]) => (
                  <div key={`prov-${label}`} data-testid={`provenance-${label.replace(/\s+/g, '-').toLowerCase()}`}>
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
                    <p className="text-xs font-medium text-slate-800 break-words">{value}</p>
                  </div>
                ))}
              </div>
              {/* One banner, one source of truth: the backend check runs
                  against the frozen listing ID, so it is preferred over the
                  panel's own comparison with the live record. */}
              {listingStatus && listingStatus.status !== 'unchanged' ? (
                <div
                  className="px-4 py-3 border-t border-amber-200 bg-amber-50 flex items-start gap-2"
                  data-testid="listing-status-line"
                >
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <span className="font-semibold">
                      {listingStatus.status === 'delisted'
                        ? 'Delisted since screening.'
                        : listingStatus.status === 'renamed'
                          ? 'Renamed since screening.'
                          : 'Cannot be re-checked.'}
                    </span>{' '}
                    {listingStatus.detail}
                    {listingStatus.name_at_screening && (
                      <> It was listed as “{listingStatus.name_at_screening}” when this
                        screening ran, and that remains the explanation of this result.</>
                    )}
                  </p>
                </div>
              ) : recordChanged && (
                <div
                  className="px-4 py-3 border-t border-amber-200 bg-amber-50 flex items-start gap-2"
                  data-testid="record-changed-since-screening"
                >
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-800 leading-relaxed">
                    The source has changed this record since the screening ran: it is now listed as
                    “{liveRecord.name}”. The screening above is unchanged and remains the
                    explanation of that result.
                  </p>
                </div>
              )}
            </div>
          )}
          {/* Personal Information */}
          {renderSection('Personal Information', User, 'personal', (
            <div className="space-y-1">
              {renderField('Birth Date', details.birth_date)}
              {renderField('Birth Place', details.birth_place)}
              {renderField('Gender', details.gender)}
              {renderField('Nationality', details.nationality || details.nationalities)}
              {renderField('Citizenship', details.citizenship || details.citizenships)}
              {renderField('Country', details.country)}
              {renderField('Religion', details.religion)}
              {renderField('Occupation', details.occupation)}
              {isOFAC && renderField('Programs', details.programs)}
              {isOFAC && renderField('Addresses', details.addresses)}
              {isUNSCR && renderField('UN Program IDs', details.program_ids)}
            </div>
          ))}

          {/* Names, each with the classification its source gave it */}
          {renderSection('Names', FileText, 'names', (
            <div className="space-y-4">
              {nameGroups.length > 0 ? (
                nameGroups.map(({ type, names }) => (
                  <div key={`name-type-${type}`} data-testid={`name-group-${type.replace(/\s+/g, '-').toLowerCase()}`}>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">
                      {type}
                      <span className="text-xs font-normal text-slate-400 ml-2">({names.length})</span>
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {names.map((name, idx) => (
                        <Badge
                          key={`${type}-${name}-${idx}`}
                          variant="outline"
                          className={`text-xs border ${nameTypeClass(type)}`}
                        >
                          {name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500">
                  This record carries no additional names.
                </p>
              )}
              <p className="text-xs text-slate-400">
                Classifications are the ones supplied by the source. Where a source gives no
                reliable type, the name is shown as “Matched name” rather than assumed to be an a.k.a.
              </p>
            </div>
          ))}

          {/* Related listings — offered, never substituted */}
          {otherListings.length > 0 && (
            <div className="border border-slate-200 rounded-md" data-testid="related-listings">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                <h3 className="text-sm font-semibold text-slate-900">
                  Related listings ({otherListings.length})
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Other authorities hold a record for this party. Each is a separate record —
                  open it explicitly to review it.
                </p>
              </div>
              <div className="divide-y divide-slate-200">
                {otherListings.map((listing, idx) => {
                  const ident = listingIdentity(listing);
                  return (
                    <div
                      key={`related-${ident.authority}-${ident.recordId}-${idx}`}
                      className="px-4 py-3 flex items-center justify-between gap-3"
                      data-testid={`related-listing-${idx}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm text-slate-900 truncate">{listing.matched_name || listing.name}</p>
                        <p className="text-xs text-slate-500 font-mono">
                          {ident.authority || 'source not recorded'} · {ident.recordId || 'no listing ID'}
                        </p>
                      </div>
                      {onOpenListing && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="shrink-0"
                          onClick={() => onOpenListing(listing)}
                          data-testid={`open-related-listing-${idx}`}
                        >
                          View record
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Family (for PEPs with Wikidata enrichment) */}
          {isPEP && (details.spouse?.length > 0 || details.children?.length > 0 || 
                     details.father?.length > 0 || details.mother?.length > 0 || 
                     details.siblings?.length > 0) && (
            renderSection('Family Relationships', Heart, 'family', (
              <div className="space-y-1">
                {renderField('Spouse', details.spouse)}
                {renderField('Father', details.father)}
                {renderField('Mother', details.mother)}
                {renderField('Children', details.children)}
                {renderField('Siblings', details.siblings)}
              </div>
            ))
          )}

          {/* Positions & Career */}
          {(details.positions?.length > 0 || details.positions_held?.length > 0) && (
            renderSection('Positions Held', Briefcase, 'positions', (
              <div className="space-y-3">
                {(details.positions_held || []).map((pos, idx) => (
                  <div key={`held-${pos}-${idx}`} className="p-3 bg-slate-50 rounded-md">
                    <p className="font-medium text-slate-900">{pos}</p>
                  </div>
                ))}
                {(details.positions || []).map((pos, idx) => (
                  <div key={`pos-${pos.title || pos}-${idx}`} className="p-3 bg-slate-50 rounded-md">
                    <p className="font-medium text-slate-900">{pos.title || pos}</p>
                    {pos.start_date && (
                      <p className="text-sm text-slate-600 mt-1">
                        {pos.start_date} - {pos.end_date || 'Present'}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ))
          )}

          {/* Additional Information */}
          {renderSection('Additional Information', Globe, 'additional', (
            <div className="space-y-4">
              {/* UNSCR Sanctions Details */}
              {isUNSCR && details.sanctions_details?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">UN Sanctions Programs</h4>
                  <div className="overflow-x-auto border border-slate-200 rounded-lg">
                    <table className="w-full text-sm" data-testid="unscr-sanctions-table">
                      <thead>
                        <tr className="bg-orange-50 border-b border-orange-200">
                          <th className="text-left px-3 py-2 font-medium text-orange-800">Program</th>
                          <th className="text-left px-3 py-2 font-medium text-orange-800">Resolution</th>
                          <th className="text-left px-3 py-2 font-medium text-orange-800">Listing Date</th>
                          <th className="text-left px-3 py-2 font-medium text-orange-800">Authority</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {details.sanctions_details.map((sd, idx) => (
                          <tr key={`sd-${sd.program_id || sd.program || idx}-${idx}`} className="hover:bg-orange-50/50">
                            <td className="px-3 py-2 text-slate-900 font-medium">{sd.program || '-'}</td>
                            <td className="px-3 py-2">
                              {sd.program_url ? (
                                <a href={sd.program_url} target="_blank" rel="noopener noreferrer"
                                   className="text-blue-600 hover:underline font-mono text-xs">
                                  {sd.program_id || '-'}
                                </a>
                              ) : (
                                <span className="font-mono text-xs text-slate-700">{sd.program_id || '-'}</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-slate-700">{sd.listing_date || sd.start_date || '-'}</td>
                            <td className="px-3 py-2 text-slate-700 text-xs">{sd.authority || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* UNSCR Program ID Badges */}
              {isUNSCR && details.program_ids?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">UN Resolution References</h4>
                  <div className="flex flex-wrap gap-2">
                    {details.program_ids.map((pid, idx) => (
                      <Badge key={`${pid}-${idx}`} className="bg-orange-100 text-orange-800 border border-orange-200 font-mono" data-testid={`entity-program-tag-${idx}`}>
                        {pid}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* UNSCR Notes */}
              {isUNSCR && details.notes?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Notes</h4>
                  <ul className="space-y-2">
                    {details.notes.map((note, idx) => (
                      <li key={`note-${idx}`} className="text-sm text-slate-600 bg-amber-50 border border-amber-100 p-2 rounded">{note}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Identification Documents Table */}
              {details.identification_documents?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Identification Documents</h4>
                  <div className="overflow-x-auto border border-slate-200 rounded-lg">
                    <table className="w-full text-sm" data-testid="id-documents-table">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="text-left px-3 py-2 font-medium text-slate-600">Type</th>
                          <th className="text-left px-3 py-2 font-medium text-slate-600">ID #</th>
                          <th className="text-left px-3 py-2 font-medium text-slate-600">Country</th>
                          <th className="text-left px-3 py-2 font-medium text-slate-600">Issue Date</th>
                          <th className="text-left px-3 py-2 font-medium text-slate-600">Expire Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {details.identification_documents.map((doc, idx) => (
                          <tr key={`id-${doc.number || doc.type || idx}-${idx}`} className="hover:bg-slate-50">
                            <td className="px-3 py-2 text-slate-900 font-medium">{doc.type || '-'}</td>
                            <td className="px-3 py-2 text-slate-700 font-mono">{doc.number || '-'}</td>
                            <td className="px-3 py-2 text-slate-700">{doc.country || '-'}</td>
                            <td className="px-3 py-2 text-slate-700">{doc.issue_date || '-'}</td>
                            <td className="px-3 py-2 text-slate-700">{doc.expiration_date || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Sanctions Information */}
              {details.sanctions_info?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Sanctions Information</h4>
                  <div className="space-y-2">
                    {details.sanctions_info.map((info, idx) => (
                      <div key={`si-${idx}`} className="text-sm text-slate-700 bg-rose-50 border border-rose-100 p-3 rounded-md">
                        {info}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Relationships */}
              {details.relationships?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Relationships</h4>
                  <div className="space-y-1">
                    {details.relationships.map((rel, idx) => (
                      <div key={`rel-${rel.type}-${rel.related_entity}-${idx}`} className="flex gap-2 text-sm py-1 border-b border-slate-100 last:border-0">
                        <span className="text-slate-500 shrink-0">{rel.type}:</span>
                        <span className="text-slate-900">{rel.related_entity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {renderField('Education', details.education)}
              {renderField('Political Party', details.political_party || details.political_association)}
              {renderField('Classification', details.classification)}
              {renderField('Topics', details.topics)}
              {renderField('Websites', details.website || details.websites)}
              {renderField('Wikidata ID', details.wikidata_id)}

              {/* Remarks */}
              {details.remarks?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Remarks</h4>
                  <ul className="space-y-2">
                    {details.remarks.map((remark, idx) => (
                      <li key={`rmk-${idx}`} className="text-sm text-slate-600 bg-slate-50 p-2 rounded">{remark}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
